import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BDPerformanceChart from './BDPerformanceChart';
import '../styles/components/insights.css';

/* --- presentational helpers (kept local to avoid cross-file deps) --- */

const Avatar = ({ name }) => {
  const initials = (name || '?')
    .split(' ')
    .map((p) => p.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return <span className="bd-cell__avatar">{initials}</span>;
};

const StatusMini = ({ bd }) => (
  <div className="status-mini">
    {bd.converted_count > 0 && (
      <span className="status-mini__item converted">✓ {bd.converted_count}</span>
    )}
    {bd.offer_awaiting_count > 0 && (
      <span className="status-mini__item offer_awaiting">⧗ {bd.offer_awaiting_count}</span>
    )}
    {bd.waiting_count > 0 && (
      <span className="status-mini__item waiting">⋯ {bd.waiting_count}</span>
    )}
    {bd.rejected_count > 0 && (
      <span className="status-mini__item rejected">✗ {bd.rejected_count}</span>
    )}
    {!bd.converted_count && !bd.rejected_count && !bd.waiting_count && !bd.offer_awaiting_count && (
      <span className="status-mini__item waiting">—</span>
    )}
  </div>
);

const RankCell = ({ rank }) => {
  if (rank > 3) return <span className="rank">#{rank}</span>;
  return <span className={`rank-medal m${rank}`}>{rank}</span>;
};

/* --- main --- */

const BDPerformanceScreen = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState('month');
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [bdStats, setBdStats] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBD, setSelectedBD] = useState(null);
  const [bdJobs, setBdJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [periodDates, setPeriodDates] = useState({ start: null, end: null });
  const [searchQuery, setSearchQuery] = useState('');

  const [showAllChart, setShowAllChart] = useState(false);
  const [showAllLeaderboard, setShowAllLeaderboard] = useState(false);
  const PREVIEW_ROWS = 6;

  useEffect(() => {
    fetchPerformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, startDate, endDate, useCustomRange]);

  const fetchPerformance = async () => {
    setLoading(true);
    try {
      const data = useCustomRange && startDate && endDate
        ? await api.getBDPerformance(null, startDate, endDate)
        : await api.getBDPerformance(period);

      setPerformanceData(data);
      setBdStats(data.bd_statistics || []);
      setPeriodDates({ start: data.period_start, end: data.period_end });
    } catch (error) {
      console.error('Failed to fetch BD performance:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateRange = (startStr, endStr) => {
    if (!startStr || !endStr) return '';
    const s = new Date(startStr);
    const e = new Date(endStr);
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', opts)}`;
  };

  const getPeriodLabel = () => {
    if (useCustomRange && startDate && endDate) return 'Custom range';
    switch (period) {
      case '24h':   return 'Last 24 hours';
      case 'week':  return 'Last 7 days';
      case 'month': return 'Last 30 days';
      case 'year':  return 'Last 12 months';
      default:      return '';
    }
  };

  const maxJobs = useMemo(() => {
    if (!bdStats.length) return 1;
    const m = Math.max(...bdStats.map((s) => s.job_count || 0));
    return m === 0 ? 1 : m;
  }, [bdStats]);

  const filteredBdStats = useMemo(
    () => bdStats.filter((bd) => bd.bd_name.toLowerCase().includes(searchQuery.toLowerCase())),
    [bdStats, searchQuery]
  );

  const handleJobCountClick = async (bdName) => {
    setSelectedBD(bdName);
    setLoadingJobs(true);
    try {
      const data = await api.getBDJobs(bdName, period);
      setBdJobs(data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch BD jobs:', error);
      setBdJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const closeModal = () => {
    setSelectedBD(null);
    setBdJobs([]);
  };

  const formatInterviewDate = (s) => {
    if (!s) return 'Not scheduled';
    const d = new Date(s);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const aggregates = useMemo(() => {
    const totalJobs = performanceData?.total_jobs || 0;
    const totalInterviews = performanceData?.total_interviews || 0;
    const activeBds = bdStats.filter((b) => (b.job_count || 0) > 0).length;
    const avgJobs = bdStats.length ? Math.round(totalJobs / bdStats.length) : 0;
    return { totalJobs, totalInterviews, activeBds, avgJobs };
  }, [performanceData, bdStats]);

  /* --- render --- */

  return (
    <div className="insights-container">
      <div className="page-header">
        <div className="page-header-text">
          <h1>BD Performance</h1>
          <p>Welcome {user?.name} — track team performance across {getPeriodLabel().toLowerCase()}.</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="insights-filter-bar">
        <div className="insights-filter-bar__left">
          <span className="insights-filter-bar__label">Period</span>
          {!useCustomRange ? (
            <div className="insights-segment" role="tablist" aria-label="Period">
              {[
                { k: '24h',   l: '24H' },
                { k: 'week',  l: 'Week' },
                { k: 'month', l: 'Month' },
                { k: 'year',  l: 'Year' },
              ].map((p) => (
                <button
                  key={p.k}
                  type="button"
                  role="tab"
                  aria-selected={period === p.k}
                  className={`insights-segment__btn ${period === p.k ? 'is-active' : ''}`}
                  onClick={() => setPeriod(p.k)}
                >
                  {p.l}
                </button>
              ))}
            </div>
          ) : (
            <div className="insights-date-range">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                max={endDate || undefined}
                aria-label="Start date"
              />
              <span className="insights-filter-bar__period">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                aria-label="End date"
              />
            </div>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setUseCustomRange((v) => !v)}
          >
            {useCustomRange ? 'Use quick filters' : 'Custom range'}
          </button>
        </div>

        <div className="insights-filter-bar__period">
          {getPeriodLabel()}
          {periodDates.start && periodDates.end && (
            <>  ·  {formatDateRange(periodDates.start, periodDates.end)}</>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-primary">
          <p className="kpi-label">Jobs in period</p>
          <p className="kpi-value">{aggregates.totalJobs.toLocaleString()}</p>
          <p className="kpi-sub">Across {bdStats.length} BDs</p>
        </div>
        <div className="kpi-card kpi-info">
          <p className="kpi-label">Interviews scheduled</p>
          <p className="kpi-value">{aggregates.totalInterviews.toLocaleString()}</p>
          <p className="kpi-sub">In this period</p>
        </div>
        <div className="kpi-card kpi-success">
          <p className="kpi-label">Active BDs</p>
          <p className="kpi-value">
            {aggregates.activeBds}<span style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xl)' }}>/{bdStats.length}</span>
          </p>
          <p className="kpi-sub">Contributing in this period</p>
        </div>
        <div className="kpi-card kpi-warning">
          <p className="kpi-label">Avg jobs / BD</p>
          <p className="kpi-value">{aggregates.avgJobs}</p>
          <p className="kpi-sub">Across the team</p>
        </div>
      </div>

      {/* Chart */}
      <div className="insights-section">
        <div className="insights-section__header">
          <div>
            <h2 className="insights-section__title">Team comparison</h2>
            <p className="insights-section__subtitle">
              Jobs, conversions, and rejections per BD.
            </p>
          </div>
          <span className="insights-section__meta">
            {aggregates.totalJobs} jobs · {aggregates.totalInterviews} interviews
          </span>
        </div>
        <div
          className={
            showAllChart
              ? 'insights-section__body'
              : 'insights-section__body insights-section__body--fixed'
          }
        >
          {loading ? (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading data…</p>
            </div>
          ) : (
            <BDPerformanceChart
              bdStats={showAllChart ? bdStats : bdStats.slice(0, PREVIEW_ROWS)}
            />
          )}
        </div>
        {!loading && bdStats.length > PREVIEW_ROWS && (
          <div className="insights-section__footer">
            <span className="insights-section__footer-meta">
              Showing {showAllChart ? bdStats.length : Math.min(PREVIEW_ROWS, bdStats.length)} of {bdStats.length} BDs
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowAllChart((v) => !v)}
            >
              {showAllChart ? 'Show less' : 'See all'}
            </button>
          </div>
        )}
      </div>

      {/* Leaderboard */}
      <div className="insights-section">
        <div className="insights-section__header">
          <div>
            <h2 className="insights-section__title">BD leaderboard</h2>
            <p className="insights-section__subtitle">
              Click a row to see their interviews for this period.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <input
              type="text"
              placeholder="Search BD by name…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ height: '34px', fontSize: 'var(--fs-sm)', minWidth: '220px' }}
            />
            <span className="insights-section__meta">
              {filteredBdStats.length} / {bdStats.length}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="insights-section__body">
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading leaderboard…</p>
            </div>
          </div>
        ) : bdStats.length === 0 ? (
          <div className="insights-section__body">
            <div className="empty-state">
              <div className="empty-state-title">No BDs to show</div>
              <div className="empty-state-desc">
                There are no active Business Development users in the system.
              </div>
            </div>
          </div>
        ) : filteredBdStats.length === 0 ? (
          <div className="insights-section__body">
            <div className="empty-state">
              <div className="empty-state-title">No matches</div>
              <div className="empty-state-desc">No BD matches “{searchQuery}”.</div>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setSearchQuery('')}
                style={{ marginTop: 'var(--space-3)' }}
              >
                Clear search
              </button>
            </div>
          </div>
        ) : (
          <>
            <div
              className={
                (showAllLeaderboard || searchQuery)
                  ? 'insights-table-wrap'
                  : 'insights-table-wrap insights-table-wrap--fixed'
              }
            >
              <table className="leaderboard">
                <thead>
                  <tr>
                    <th className="rank">#</th>
                    <th>BD</th>
                    <th className="bar-cell">Share</th>
                    <th className="num">Jobs</th>
                    <th className="num">Interviews</th>
                    <th className="num">Avg Rounds</th>
                    <th>Status mix</th>
                    <th className="num">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllLeaderboard || searchQuery
                    ? filteredBdStats
                    : filteredBdStats.slice(0, PREVIEW_ROWS)
                  ).map((bd, idx) => {
                    const pct = (bd.job_count / maxJobs) * 100;
                    return (
                      <tr
                        key={bd.bd_name}
                        onClick={() => handleJobCountClick(bd.bd_name)}
                        title="View interviews"
                      >
                        <td className="rank">
                          <RankCell rank={idx + 1} />
                        </td>
                        <td>
                          <div className="bd-cell">
                            <Avatar name={bd.bd_name} />
                            <div>
                              <div className="bd-cell__name">{bd.bd_name}</div>
                              <span className="bd-cell__meta">{bd.share_pct || 0}% of team</span>
                            </div>
                          </div>
                        </td>
                        <td className="bar-cell">
                          <div className="mini-bar">
                            <div className="mini-bar__fill" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="mini-bar__meta">
                            <span>{bd.job_count}</span>
                            <span>{pct.toFixed(0)}%</span>
                          </div>
                        </td>
                        <td className="num">{bd.job_count}</td>
                        <td className="num">{bd.interview_count || 0}</td>
                        <td className="num">{bd.avg_rounds_per_job || 0}</td>
                        <td>
                          <StatusMini bd={bd} />
                        </td>
                        <td className="num">
                          {(bd.converted_count || 0) + (bd.rejected_count || 0)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!searchQuery && filteredBdStats.length > PREVIEW_ROWS && (
              <div className="insights-section__footer">
                <span className="insights-section__footer-meta">
                  Showing {showAllLeaderboard ? filteredBdStats.length : Math.min(PREVIEW_ROWS, filteredBdStats.length)} of {filteredBdStats.length} BDs
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowAllLeaderboard((v) => !v)}
                >
                  {showAllLeaderboard ? 'Show less' : 'See all'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Drilldown modal */}
      {selectedBD && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="bd-jobs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Interviews by {selectedBD} · {getPeriodLabel()}</h3>
              <button className="modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              {loadingJobs ? (
                <div className="loading-state">
                  <div className="spinner" />
                  <p>Loading interviews…</p>
                </div>
              ) : bdJobs.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-title">No interviews</div>
                  <div className="empty-state-desc">No interviews found for this period.</div>
                </div>
              ) : (
                <div className="bd-jobs-list">
                  {bdJobs.map((job) => (
                    <div key={job.id} className="bd-job-item">
                      <div className="job-main-info">
                        <span className="job-company">{job.company_name}</span>
                        <span className="job-position">
                          First interview: {formatInterviewDate(job.first_interview_date)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BDPerformanceScreen;
